'use client';

import { useCallback, useEffect, useState } from 'react';

const FAVORITES_UPDATED_EVENT = 'favorites-updated';

interface FavoritesUpdatedDetail {
  storageKey: string;
  favorites: string[];
}

export interface UseFavoritesReturn {
  favorites: Set<string>;
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
  isInitialized: boolean;
}

/**
 * Generic hook for managing favorites with localStorage persistence.
 * @param storageKey - The localStorage key to use for persistence
 */
export function useFavorites(storageKey: string): UseFavoritesReturn {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const updateFavorites = (storedValue: string | null) => {
      if (!storedValue) {
        setFavorites(new Set());
        return;
      }

      try {
        const parsed = JSON.parse(storedValue) as unknown;
        const favoriteIds = Array.isArray(parsed)
          ? parsed.filter((id): id is string => typeof id === 'string')
          : [];
        setFavorites(new Set(favoriteIds));
      } catch {
        setFavorites(new Set());
      }
    };

    try {
      updateFavorites(localStorage.getItem(storageKey));
    } catch {
    }
    setIsInitialized(true);

    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        updateFavorites(event.newValue);
      }
    };

    const handleFavoritesUpdated = (event: Event) => {
      const detail = (event as CustomEvent<FavoritesUpdatedDetail>).detail;
      if (detail?.storageKey === storageKey) {
        setFavorites(new Set(detail.favorites));
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    };
  }, [storageKey]);

  const persistFavorites = useCallback(
    (newFavorites: Set<string>) => {
      try {
        const favorites = [...newFavorites];
        localStorage.setItem(storageKey, JSON.stringify(favorites));
        window.dispatchEvent(
          new CustomEvent<FavoritesUpdatedDetail>(FAVORITES_UPDATED_EVENT, {
            detail: { storageKey, favorites }
          })
        );
      } catch {
      }
    },
    [storageKey]
  );

  const toggleFavorite = useCallback(
    (id: string) => {
      const next = new Set(favorites);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setFavorites(next);
      persistFavorites(next);
    },
    [favorites, persistFavorites]
  );

  const isFavorite = useCallback((id: string) => favorites.has(id), [favorites]);

  return { favorites, toggleFavorite, isFavorite, isInitialized };
}
